import { createAsyncTypes } from 'store/utils';

// State Key
export const KEY = 'kiosk';

// Action Types
export const types = {
  UPDATE_KIOSK_ASYNC: createAsyncTypes(`${KEY}/UPDATE_KIOSK_ASYNC`),
  GET_KIOSK_ASYNC: createAsyncTypes(`${KEY}/GET_KIOSK_ASYNC`),
  REBOOT_KIOSK_ASYNC: createAsyncTypes(`${KEY}/REBOOT_KIOSK_ASYNC`),
  REBOOT_ADMIN_KIOSK_ASYNC: createAsyncTypes(`${KEY}/REBOOT_ADMIN_KIOSK_ASYNC`),
  UPDATE_KIOSK_HARDWARE_ASYNC: createAsyncTypes(`${KEY}/UPDATE_KIOSK_HARDWARE_ASYNC`),
  CREATE_PRINT_TICKETS_ASYNC: createAsyncTypes(`${KEY}/CREATE_PRINT_TICKETS_ASYNC`),
  CREATE_ORDER_TICKETS_ASYNC: createAsyncTypes(`${KEY}/CREATE_ORDER_TICKETS_ASYNC`),
  UPDATE_TRANSACTIONS_ASYNC: createAsyncTypes(`${KEY}/UPDATE_TRANSACTIONS_ASYNC`),
  CHECK_SCHEDULE: {
    REQUEST: `${KEY}/CHECK_SCHEDULE_REQUEST`,
    SUCCESS: `${KEY}/CHECK_SCHEDULE_SUCCESS`
  },
  LISTEN_SOCKET_SERVER: `${KEY}/LISTEN_SOCKET_SERVER`,
  RESET_TICKETS: `${KEY}/RESET_TICKETS`,
  DECREMENT_STOCK_COUNTS_ASYNC: createAsyncTypes(`${KEY}/DECREMENT_STOCK_COUNTS_ASYNC`),
  CHECK_LOW_STOCK_THRESHOLDS: `${KEY}/CHECK_LOW_STOCK_THRESHOLDS`,
  RELOAD_APPLICATION: `${KEY}/RELOAD_APPLICATION`,
  CHECK_KIOSK_VERSION_ASYNC: createAsyncTypes(`${KEY}/CHECK_KIOSK_VERSION_ASYNC`),
  UPDATE_HARDWARE_STATUS: `${KEY}/UPDATE_HARDWARE_STATUS`,
  SET_STATUS_ACTOR: `${KEY}/SET_STATUS_ACTOR`,
  RESET_KIOSK: `${KEY}/RESET_KIOSK`
};

export interface Ticket {
  message: string;
  ticketNumber?: string;
  transactionId?: string;
  kioskHardwareId?: string;
}

export interface Area {
  id: string;
  venueId: string;
  name: string | undefined;
  description: string;
  createdAt: string;
  updatedAt: string;
}

export interface Client {
  id: string;
  name: string;
}

export interface Venue {
  id: string;
  clientId: string;
  integrationId: string;
  name: string;
  address: string;
}

export type HardwareTypes = 'printer' | 'barcodeScanner' | 'paymentDevice';

export type StatusActor =
  | 'kcc'
  | 'maintenance'
  | 'schedule'
  | 'paymentDeviceError'
  | 'printerError'
  | 'scannerError'
  | 'lowStock'
  | 'selfHeal';

export interface KioskHardware {
  readonly id: string;
  readonly kioskId: string;
  readonly hardwareId: string;
  readonly serialCode: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly type: HardwareTypes;
  readonly name: string;
  readonly code: string;
  readonly imageUrl: ImageUrl;
  readonly status: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  readonly details: any;
  readonly configuration: any;
  readonly values: {
    readonly currentStock?: number;
  };
}

export interface TicketTemplate {
  readonly id: string;
  readonly clientId: string;
  readonly type: string;
  readonly template: string;
  readonly name: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface ImageUrl {
  readonly primary: string;
  readonly secondary: string;
  readonly tertiary: string;
}

export interface KioskConfiguration {
  readonly disableHardwarePing?: string[];
  readonly kioskPreview?: {
    readonly enabled?: boolean;
    readonly allowCheckOut?: boolean;
  };
}

export interface Kiosk {
  readonly id: string;
  readonly areaId: string;
  readonly configurationId: string;
  readonly name: string;
  readonly status: string;
  readonly macAddress: string;
  readonly ipAddress: string;
  readonly client: Client;
  readonly venue: Venue;
  readonly area: Area;
  readonly hardware: KioskHardware[];
  readonly ticketTemplates: TicketTemplate[];
  readonly schedule?: KioskSchedule;
  readonly setupComplete: boolean;
  readonly unscheduledUntil: Date | string | null;
  readonly inactiveKioskHardwareId: string | undefined;
  readonly configuration: KioskConfiguration;
  readonly serialNumber: string | null;
  readonly updatedAt: string;
  readonly createdAt: string;
}

export interface KioskState {
  readonly kiosk: Kiosk | null;
  readonly tickets: Ticket[];
  readonly loading: boolean;
  readonly error: undefined | string;
  readonly version: string | undefined;
  readonly statusActor: StatusActor | undefined;
  readonly kioskWaiting: boolean;
  readonly kioskRebootWaiting: boolean;
  readonly inactiveKioskHardwareId: string | undefined;
}

export type KioskSchedule = KioskScheduleDay[];

export interface KioskScheduleDay {
  id: string;
  kioskId: string;
  day: number;
  startTime: number;
  endTime: number;
}

// Print Tickets
export interface CreatePrintTicketsAsyncRequest {
  type: typeof types.CREATE_PRINT_TICKETS_ASYNC.REQUEST;
  payload: {
    orderId: string;
    transactionId: string;
    deliveryParams?: any;
  };
}

export interface CreatePrintTicketsAsyncError {
  type: typeof types.CREATE_PRINT_TICKETS_ASYNC.ERROR;
  payload: string;
}

export interface CreatePrintTicketsAsyncPending {
  type: typeof types.CREATE_PRINT_TICKETS_ASYNC.PENDING;
  payload: boolean;
}

export interface CreatePrintTicketsAsyncSuccess {
  type: typeof types.CREATE_PRINT_TICKETS_ASYNC.SUCCESS;
  payload: Ticket[];
}

// Print Order Tickets
export interface CreateOrderTicketsAsyncRequest {
  type: typeof types.CREATE_ORDER_TICKETS_ASYNC.REQUEST;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  payload: any[];
}

export interface CreateOrderTicketsAsyncError {
  type: typeof types.CREATE_ORDER_TICKETS_ASYNC.ERROR;
  payload: string;
}

export interface CreateOrderTicketsAsyncPending {
  type: typeof types.CREATE_ORDER_TICKETS_ASYNC.PENDING;
  payload: boolean;
}

export interface CreateOrderTicketsAsyncSuccess {
  type: typeof types.CREATE_ORDER_TICKETS_ASYNC.SUCCESS;
  payload: Ticket[];
}

// Get Kiosk
export interface GetKioskAsyncRequest {
  type: typeof types.GET_KIOSK_ASYNC.REQUEST;
  payload: {
    kioskId: string;
  };
}

export interface GetKioskAsyncError {
  type: typeof types.GET_KIOSK_ASYNC.ERROR;
  payload: string;
}

export interface GetKioskAsyncPending {
  type: typeof types.GET_KIOSK_ASYNC.PENDING;
  payload: boolean;
}

export interface GetKioskAsyncSuccess {
  type: typeof types.GET_KIOSK_ASYNC.SUCCESS;
  payload: Kiosk;
}

// Update Kiosk
export interface UpdateKioskAsyncRequest {
  type: typeof types.UPDATE_KIOSK_ASYNC.REQUEST;
  payload: Partial<Kiosk>;
}

export interface UpdateKioskAsyncError {
  type: typeof types.UPDATE_KIOSK_ASYNC.ERROR;
  payload: string;
}

export interface UpdateKioskAsyncPending {
  type: typeof types.UPDATE_KIOSK_ASYNC.PENDING;
  payload: boolean;
}

export interface UpdateKioskAsyncSuccess {
  type: typeof types.UPDATE_KIOSK_ASYNC.SUCCESS;
  payload: Kiosk;
}

export interface UpdateKioskAsyncWaiting {
  type: typeof types.UPDATE_KIOSK_ASYNC.WAITING;
  payload: boolean;
}

export interface RebootAdminKioskAsyncWaiting {
  type: typeof types.REBOOT_ADMIN_KIOSK_ASYNC.WAITING;
  payload: boolean;
}

// Reboot Kiosk
export interface RebootKioskAsyncRequest {
  type: typeof types.REBOOT_KIOSK_ASYNC.REQUEST;
}

export interface RebootKioskAsyncError {
  type: typeof types.REBOOT_KIOSK_ASYNC.ERROR;
  payload: string;
}

export interface RebootKioskAsyncPending {
  type: typeof types.REBOOT_KIOSK_ASYNC.PENDING;
  payload: boolean;
}

export interface RebootKioskAsyncSuccess {
  type: typeof types.REBOOT_KIOSK_ASYNC.SUCCESS;
}

// Reboot Kiosk
export interface RebootAdminKioskAsyncRequest {
  type: typeof types.REBOOT_ADMIN_KIOSK_ASYNC.REQUEST;
}

export interface RebootAdminKioskAsyncError {
  type: typeof types.REBOOT_ADMIN_KIOSK_ASYNC.ERROR;
  payload: string;
}

export interface RebootAdminKioskAsyncPending {
  type: typeof types.REBOOT_ADMIN_KIOSK_ASYNC.PENDING;
  payload: boolean;
}

export interface RebootAdminKioskAsyncSuccess {
  type: typeof types.REBOOT_ADMIN_KIOSK_ASYNC.SUCCESS;
}

export interface UpdateKioskHardwareAsyncRequest {
  type: typeof types.UPDATE_KIOSK_HARDWARE_ASYNC.REQUEST;
  payload: {
    id: string;
    values: {
      currentStock: number;
    };
  };
}

export interface UpdateKioskHardwareAsyncSuccess {
  type: typeof types.UPDATE_KIOSK_HARDWARE_ASYNC.SUCCESS;
  payload: KioskHardware;
}

export interface UpdateKioskHardwareAsyncPending {
  type: typeof types.UPDATE_KIOSK_HARDWARE_ASYNC.PENDING;
  payload: boolean;
}

export interface UpdateKioskHardwareAsyncError {
  type: typeof types.UPDATE_KIOSK_HARDWARE_ASYNC.ERROR;
  payload: string;
}

export interface DecrementStockCountsAsyncRequest {
  type: typeof types.DECREMENT_STOCK_COUNTS_ASYNC.REQUEST;
}

export interface UpdateTransactionsAsyncRequest {
  type: typeof types.UPDATE_TRANSACTIONS_ASYNC.REQUEST;
  payload: {
    transactionId: string;
    deliveryFailedTicketCount: number | null;
  }[];
}

// Check Schedule
export interface CheckScheduleRequest {
  type: typeof types.CHECK_SCHEDULE.REQUEST;
}

export interface CheckScheduleSuccess {
  type: typeof types.CHECK_SCHEDULE.SUCCESS;
  payload: string;
}

// Listen Kiosk Update Server
export interface ListenKioskUpdateServer {
  type: typeof types.LISTEN_SOCKET_SERVER;
  payload: { kioskId: string; configurationId: string; clientId: string };
}

export interface CheckKioskVersionRequest {
  type: typeof types.CHECK_KIOSK_VERSION_ASYNC.REQUEST;
}

export interface CheckKioskVersionAsyncSuccess {
  type: typeof types.CHECK_KIOSK_VERSION_ASYNC.SUCCESS;
  payload: string;
}

export interface CheckKioskVersionAsyncPending {
  type: typeof types.CHECK_KIOSK_VERSION_ASYNC.PENDING;
  payload: boolean;
}

export interface SetStatusActor {
  type: typeof types.SET_STATUS_ACTOR;
  payload: StatusActor;
}

export interface UpdateHardwareStatus {
  type: typeof types.UPDATE_HARDWARE_STATUS;
  payload: {
    hardwareId: string;
    status: string;
  };
}

export type KioskActionTypes =
  | CreatePrintTicketsAsyncRequest
  | CreatePrintTicketsAsyncError
  | CreatePrintTicketsAsyncPending
  | CreatePrintTicketsAsyncSuccess
  | CreateOrderTicketsAsyncRequest
  | CreateOrderTicketsAsyncError
  | CreateOrderTicketsAsyncPending
  | CreateOrderTicketsAsyncSuccess
  | GetKioskAsyncRequest
  | GetKioskAsyncError
  | GetKioskAsyncPending
  | GetKioskAsyncSuccess
  | UpdateKioskAsyncRequest
  | UpdateKioskAsyncError
  | UpdateKioskAsyncPending
  | UpdateKioskAsyncSuccess
  | UpdateKioskAsyncWaiting
  | RebootAdminKioskAsyncWaiting
  | RebootKioskAsyncRequest
  | RebootKioskAsyncError
  | RebootKioskAsyncPending
  | RebootKioskAsyncSuccess
  | RebootAdminKioskAsyncRequest
  | RebootAdminKioskAsyncError
  | RebootAdminKioskAsyncPending
  | RebootAdminKioskAsyncSuccess
  | DecrementStockCountsAsyncRequest
  | CheckScheduleRequest
  | CheckScheduleSuccess
  | ListenKioskUpdateServer
  | CheckKioskVersionRequest
  | CheckKioskVersionAsyncPending
  | CheckKioskVersionAsyncSuccess
  | SetStatusActor
  | UpdateHardwareStatus;
